import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';

import { useAtom } from 'jotai';
import { RESET, atomWithStorage } from 'jotai/utils';

export const COLOR_MODE_KEY = 'bmt-color-mode';
export type ColorMode = 'light' | 'dark';

const preferredColorModeAtom = atomWithStorage<ColorMode | undefined>(COLOR_MODE_KEY, undefined, undefined, {
  getOnInit: true,
});

const systemColorSchemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

const subscribeToSystemColorScheme = (onChange: () => void) => {
  systemColorSchemeMediaQuery.addEventListener('change', onChange);
  return () => systemColorSchemeMediaQuery.removeEventListener('change', onChange);
};

const getSystemColorMode = (): ColorMode => {
  return systemColorSchemeMediaQuery.matches ? 'dark' : 'light';
};

type ColorModeContextValue = {
  colorMode: ColorMode;
  setColorMode: (colorMode: ColorMode) => void;
  matchSystem: () => void;
  isEnabled: boolean;
};

const defaultContextValue: ColorModeContextValue = {
  colorMode: 'light',
  setColorMode: () => {},
  matchSystem: () => {},
  isEnabled: false,
};

const ColorModeContext = createContext<ColorModeContextValue>(defaultContextValue);

interface ColorModeProviderProps {
  children: ReactNode;
  isEnabled?: boolean;
}

export const ColorModeProvider = ({ children, isEnabled = true }: ColorModeProviderProps) => {
  const [preferredColorMode, setPreferredColorMode] = useAtom(preferredColorModeAtom);
  const matchSystem = useCallback(() => setPreferredColorMode(RESET), [setPreferredColorMode]);

  const systemColorMode = useSyncExternalStore(subscribeToSystemColorScheme, getSystemColorMode, () => 'light');
  const resolvedColorMode = (isEnabled ? (preferredColorMode ?? systemColorMode) : 'light') as ColorMode;

  useEffect(() => {
    if (isEnabled) {
      document.documentElement.setAttribute('data-color-mode', resolvedColorMode);
    } else {
      document.documentElement.removeAttribute('data-color-mode');
    }
  }, [isEnabled, resolvedColorMode]);

  const contextValue: ColorModeContextValue = useMemo(
    () => ({
      colorMode: resolvedColorMode,
      setColorMode: setPreferredColorMode,
      matchSystem,
      isEnabled,
    }),
    [resolvedColorMode, setPreferredColorMode, matchSystem, isEnabled]
  );

  return <ColorModeContext.Provider value={contextValue}>{children}</ColorModeContext.Provider>;
};

export const useColorMode = (): ColorModeContextValue => {
  return useContext(ColorModeContext);
};
